import React from 'react';
import { Box, IconButton, Paper, TextField, Typography } from '@mui/material';
import { Delete, DragIndicator } from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { mb } from '../../common/theme/mbTokens';

export const SHORTCUT_TITLE_MAX_LENGTH = 60;
export const SHORTCUT_TEXT_MAX_LENGTH = 1000;

// Same counting as the api: code points of the trimmed value.
export const getShortcutLength = (value: string): number => [...value.trim()].length;

type SortableShortcutCardProps = {
  rowKey: string;
  title: string;
  text: string;
  titleError?: string;
  messageError?: string;
  onTitleChange: (title: string) => void;
  onTextChange: (text: string) => void;
  onDelete: () => void;
};

export const SortableShortcutCard = ({
  rowKey,
  title,
  text,
  titleError,
  messageError,
  onTitleChange,
  onTextChange,
  onDelete,
}: SortableShortcutCardProps) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({
    id: rowKey,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const textLength = getShortcutLength(text);
  const isTextTooLong = textLength > SHORTCUT_TEXT_MAX_LENGTH;

  return (
    <div ref={setNodeRef} style={style}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          display: 'grid',
          gap: 1.5,
          alignItems: 'start',
          gridTemplateColumns: { xs: '1fr auto', md: 'auto 220px 1fr auto' },
          gridTemplateAreas: {
            xs: '"handle delete" "title title" "message message"',
            md: '"handle title message delete"',
          },
        }}>
        <IconButton
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label={t('extensionSettings.shortcuts.dragAria')}
          sx={{
            gridArea: 'handle',
            justifySelf: 'start',
            cursor: 'grab',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
            width: { xs: 44, md: 40 },
            height: { xs: 44, md: 40 },
          }}>
          <DragIndicator sx={{ color: 'text.secondary' }} />
        </IconButton>
        <TextField
          size="small"
          fullWidth
          sx={{ gridArea: 'title' }}
          label={t('extensionSettings.shortcuts.titleLabel')}
          value={title}
          onChange={(event) => onTitleChange(event.target.value)}
          error={Boolean(titleError)}
          helperText={titleError}
        />
        <Box sx={{ gridArea: 'message', minWidth: 0 }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t('extensionSettings.shortcuts.messageLabel')}
            value={text}
            onChange={(event) => onTextChange(event.target.value)}
            error={Boolean(messageError)}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.5, px: 1.75 }}>
            <Typography variant="caption" sx={{ color: mb.danger }}>
              {messageError}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: isTextTooLong ? mb.danger : 'text.secondary', marginInlineStart: 'auto' }}>
              {textLength}/{SHORTCUT_TEXT_MAX_LENGTH}
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label={t('extensionSettings.shortcuts.deleteAria')}
          onClick={onDelete}
          sx={{
            gridArea: 'delete',
            justifySelf: 'end',
            width: { xs: 44, md: 40 },
            height: { xs: 44, md: 40 },
          }}>
          <Delete />
        </IconButton>
      </Paper>
    </div>
  );
};
